import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import SyncIcon from '@mui/icons-material/Sync';

import {
  ColumnProps,
  Page,
  PageHeaderAction,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import { stringify } from 'utils/base64';
import { getPageName } from 'utils/format';
import { RouteNames } from 'config/routeNames';
import { CommentCodeModel, CommentCodeType, FilterOptionModel } from 'models';
import { getYesOrNoLabel } from 'pages/Leo/ReadingUnitDetails/ReadingUnitDetailsPage';
import { useCommentCodesList } from 'features/commentCodes';
import { getFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { useValueSetList } from 'features/valueSets';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { useCanEdit } from 'utils/auth';

const commentCodeTypeTranslationMap = {
  [CommentCodeType.READING_FAILURE_REASON]: 'LEO_MEGJEGYZES_KODOK.TYPE_NLO',
  [CommentCodeType.REPORT]: 'LEO_MEGJEGYZES_KODOK.TYPE_REPORT',
};

export const CommentCodeListPage = () => {
  const navigate = useNavigate();
  const canEditCommentCodes = useCanEdit('READING_COMMENT_CODES');

  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    onFilter,
    onLoadQuery,
    params,
    apiParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: stringify([{ orderBy: 'order' }]),
  });

  const { data: listData, isLoading, isFetching, dataUpdatedAt } = useCommentCodesList(apiParams);

  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['KT'],
  });

  const { accessor: publicUtilityTypeAccessor } = getFieldDataAndAccessor(
    getValueSetValues(valueSet, 'KT')
  );

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'LEO_MEGJEGYZES_KODOK.VALUE',
      component: (
        <LeoTextField name="name" label={<Trans i18nKey="LEO_MEGJEGYZES_KODOK.VALUE" />} />
      ),
      name: 'name',
      panelFieldWidth: 12,
      abbrName: t('LEO_MEGJEGYZES_KODOK.VALUE'),
    },
  ];

  const { activePanel, closePanel, setPanel } = usePanelState();

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.LEO_MEGJEGYZES_KODOK),
    isFetching,
  });

  const panels = [filterConfig];

  const columns: ColumnProps<CommentCodeModel>[] = [
    {
      key: 'type',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.TYPE" />,
      accessor: ({ type }) => <Trans i18nKey={commentCodeTypeTranslationMap[type]} />,
      field: 'type',
      sortable: true,
    },
    {
      key: 'utilityType',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.UTILITY_TYPE" />,
      accessor: ({ utilityType }) => publicUtilityTypeAccessor[utilityType],
      field: 'utilityType',
      sortable: true,
    },
    {
      key: 'code',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.CODE" />,
      accessor: 'code',
      sortable: true,
    },
    {
      key: 'value',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.VALUE" />,
      accessor: 'value',
      sortable: true,
    },
    {
      key: 'order',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.ORDER" />,
      accessor: 'order',
      sortable: true,
    },
    {
      key: 'isPhotoRequired',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.IS_PHOTO_REQUIRED" />,
      accessor: ({ isPhotoRequired }) => getYesOrNoLabel(isPhotoRequired),
      field: 'isPhotoRequired',
      sortable: true,
    },
    {
      key: 'minPhoto',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.MIN_PHOTO" />,
      accessor: 'minPhoto',
      sortable: true,
    },
    {
      key: 'maxPhoto',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.MAX_PHOTO" />,
      accessor: 'maxPhoto',
      sortable: true,
    },
    {
      key: 'isFinal',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.IS_FINAL" />,
      accessor: ({ isFinal }) => getYesOrNoLabel(isFinal),
      field: 'isFinal',
      sortable: true,
    },
    {
      key: 'validForConsumptionPlace',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.VALID_FOR_CONSUMPTION_PLACE" />,
      accessor: ({ validForConsumptionPlace }) => getYesOrNoLabel(validForConsumptionPlace),
      field: 'validForConsumptionPlace',
      sortable: true,
    },
    {
      key: 'validForDevice',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.VALID_FOR_DEVICE" />,
      accessor: ({ validForDevice }) => getYesOrNoLabel(validForDevice),
      field: 'validForDevice',
      sortable: true,
    },
    {
      key: 'validForConsumptionMeter',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.VALID_FOR_CONSUMPTION_METER" />,
      accessor: ({ validForConsumptionMeter }) => getYesOrNoLabel(validForConsumptionMeter),
      field: 'validForConsumptionMeter',
      sortable: true,
    },
    {
      key: 'isActive',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.IS_ACTIVE" />,
      accessor: ({ isActive }) => getYesOrNoLabel(isActive),
      field: 'isActive',
      sortable: true,
    },
    {
      key: 'isCommentRequired',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.IS_COMMENT_REQUIRED" />,
      accessor: ({ isCommentRequired }) => getYesOrNoLabel(isCommentRequired),
      field: 'isCommentRequired',
      sortable: true,
    },
    {
      key: 'taskType',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.TASK_TYPE" />,
      accessor: 'taskType',
      field: 'taskType',
      sortable: true,
    },
    {
      key: 'cardRequired',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.CARD_REQUIRED" />,
      accessor: ({ cardRequired }) => getYesOrNoLabel(cardRequired),
      field: 'cardRequired',
      sortable: true,
    },
    {
      key: 'readingAvailable',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.READING_AVAILABLE" />,
      accessor: ({ readingAvailable }) => getYesOrNoLabel(readingAvailable),
      field: 'readingAvailable',
      sortable: true,
    },
    {
      key: 'lowConsumption',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.LOW_CONSUMPTION" />,
      accessor: ({ lowConsumption }) => getYesOrNoLabel(lowConsumption),
      field: 'lowConsumption',
      sortable: true,
    },
    {
      key: 'noConsumption',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.NO_CONSUMPTION" />,
      accessor: ({ noConsumption }) => getYesOrNoLabel(noConsumption),
      field: 'noConsumption',
      sortable: true,
    },
    {
      key: 'normalConsumption',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.NORMAL_CONSUMPTION" />,
      accessor: ({ normalConsumption }) => getYesOrNoLabel(normalConsumption),
      field: 'normalConsumption',
      sortable: true,
    },
    {
      key: 'highConsumption',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK.HIGH_CONSUMPTION" />,
      accessor: ({ highConsumption }) => getYesOrNoLabel(highConsumption),
      field: 'highConsumption',
      sortable: true,
    },
  ];

  const headerActions = useMemo<PageHeaderAction[]>(
    () => [
      {
        name: 'transfer',
        label: 'LEO_MEGJEGYZES_KODOK.TRANSFER_BUTTON',
        icon: <SyncIcon fill="currentColor" />,
        onClick: () => navigate(`/${RouteNames.LEO_MEGJEGYZES_KODOK_ATVETEL}`),
        visible: canEditCommentCodes,
      },
    ],
    [navigate, canEditCommentCodes]
  );

  return (
    <Page
      title={<Trans i18nKey="LEO_MEGJEGYZES_KODOK.TITLE" />}
      inScroll
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
      panels={panels}
    >
      {!isValueSetLoading && (
        <Table
          panels={panels}
          setPanel={setPanel}
          activePanel={activePanel}
          timestamp={dataUpdatedAt}
          list={listData?.data || []}
          total={listData?.meta?.total}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={columns}
          pageHeaderActions={headerActions}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          onLoadQuery={onLoadQuery}
        />
      )}
    </Page>
  );
};
